/**
 * WeightBar — horizontal bar visualising a criterion's weight as a fraction
 * of the rubric's total target (100%). Colour reflects the rubric's overall
 * health, not the individual row:
 *
 *   total === 100   blue    (rubric balanced)
 *   total <  100    amber   (rubric under)
 *   total >  100    red     (rubric over)
 *
 * Width is the row's weight as a percentage of 100, capped at 100% so a
 * single row that's already over 100 doesn't blow out the layout.
 *
 * Linear: MYD-208 (E3).
 */

import { cn } from 'lib/utils'

interface WeightBarProps {
	weight: number
	totalWeight: number
	className?: string
}

const WeightBar = ({ weight, totalWeight, className }: WeightBarProps) => {
	const safeWeight = Number.isFinite(weight) && weight > 0 ? weight : 0
	const widthPct = Math.min(100, Math.max(0, safeWeight))

	const colour =
		totalWeight === 100
			? 'bg-[#0080fe]'
			: totalWeight > 100
				? 'bg-red-500'
				: 'bg-amber-500'

	return (
		<div
			className={cn(
				'h-1.5 w-full rounded-full bg-gray-100 overflow-hidden',
				className,
			)}
			role="presentation"
			aria-hidden
		>
			<div
				className={cn('h-full rounded-full transition-all duration-150', colour)}
				style={{ width: `${widthPct}%` }}
			/>
		</div>
	)
}

export default WeightBar
